import { titleCase } from 'change-case'

/**
 * Display parts of a webhook event type identifier.
 *
 * A webhook event type is `subject.action` (e.g. `push_subscription.created`), where
 * `subject` is everything before the first period and `action` everything after.
 */
export interface WebhookEventTypeLabelParts {
    /** The event subject, space-separated Title Case. e.g. `push_subscription` -> `Push Subscription`. */
    subject: string
    /** The event action, space-separated Title Case. e.g. `created` -> `Created`. */
    action: string
}

/**
 * Split a webhook event type identifier into its Title-Cased subject and action.
 *
 * Uses `titleCase` from change-case so multi-word subjects render with spaces:
 * `push_subscription.created` -> `{ subject: 'Push Subscription', action: 'Created' }`.
 *
 * This is the single source of truth for webhook event type label formatting — both the
 * list tag and the drawer's grouped Select derive from it, so the two sites can never
 * format the same identifier differently again.
 */
export const getWebhookEventTypeLabelParts = (eventType: string): WebhookEventTypeLabelParts => {
    const [subject = '', action = ''] = eventType.split('.')
    return { subject: titleCase(subject), action: titleCase(action) }
}

/**
 * Format a webhook event type identifier as a single display label.
 *
 * `push_subscription.created` -> `Push Subscription Created`. Derived from
 * {@link getWebhookEventTypeLabelParts} so it stays consistent with the drawer labels.
 */
export const formatWebhookEventTypeLabel = (eventType: string): string => {
    const { subject, action } = getWebhookEventTypeLabelParts(eventType)
    return action ? `${subject} ${action}` : subject
}
